import { Controller } from "@hotwired/stimulus"
import { htmlToMarkdown } from "../utils/html_to_markdown"
import { writeClipboard } from "../utils/clipboard"

export default class extends Controller {
  static targets = [ "source" ]

  copy(event) {
    const trigger = event.currentTarget
    writeClipboard(this._clipboardContent(event)).then(() => this._onCopied(trigger))
  }

  _onCopied(trigger) {
    this.dispatch("copy")
    if (trigger?.getAttribute("role") !== "tooltip") return

    if (!trigger.dataset.originalLabel) {
      trigger.dataset.originalLabel = trigger.getAttribute("aria-label")
      trigger.addEventListener("mouseleave", () => {
        trigger.setAttribute("aria-label", trigger.dataset.originalLabel)
        delete trigger.dataset.originalLabel
      }, { once: true })
    }
    trigger.setAttribute("aria-label", I18n.events.copied)
  }

  _clipboardContent(event) {
    if (event.params.text) return { text: event.params.text }
    if (event.params.format === "markdown") {
      const source = this._sourceWithoutPreviewPills()
      return {
        html: this._htmlForClipboard(source),
        text: htmlToMarkdown(source)
      }
    }

    // If you don't specify the text to be copied, it uses the sourceTarget text content.
    return { text: this.sourceTarget.innerText || this.sourceTarget.textContent || "" }
  }

  // Article preview pills are UI controls injected by the auto-linker; they must
  // not leak into copied content. Clone the source and drop them before serializing.
  _sourceWithoutPreviewPills() {
    const clone = this.sourceTarget.cloneNode(true)
    clone.querySelectorAll(".article-preview-pill").forEach(pill => pill.remove())
    return clone
  }

  _htmlForClipboard(element) {
    const clone = element.cloneNode(true)
    this._prepareTablesForClipboard(clone)

    return clone.innerHTML
  }

  _prepareTablesForClipboard(element) {
    element.querySelectorAll("table").forEach(table => {
      this._mergeStyles(table, {
        width: "100%",
        "border-collapse": "collapse",
        "table-layout": "auto",
        "margin-bottom": "16px"
      })
      table.setAttribute("width", "100%")

      const firstRow = table.querySelector("tr")
      const firstRowCells = firstRow ? firstRow.querySelectorAll("th, td") : []
      const cellWidth = firstRowCells.length > 0 ? `${100 / firstRowCells.length}%` : null

      table.querySelectorAll("th, td").forEach(cell => {
        this._mergeStyles(cell, {
          border: "1px solid #d1d5db",
          padding: "6px 10px",
          "text-align": "left",
          "vertical-align": "top"
        })
      })

      firstRowCells.forEach(cell => {
        this._mergeStyles(cell, { width: cellWidth })
        cell.setAttribute("width", cellWidth)
      })
    })
  }

  _mergeStyles(element, styles) {
    Object.entries(styles).forEach(([property, value]) => {
      if (value) element.style.setProperty(property, value)
    })
  }
}
